<script setup>import { ref as _ref, computed as _computed } from 'vue';

  import { watch } from "vue";

  import IconHint from "./icon-hint.vue";
  import {
    CURRENCIES,
    EXPENSE_TYPES,
    UNIT_FAMILIES,
    buildCsv,
    compatibleUnits,
    costBreakdown,
    currencySymbol,
    defaultUnitForType,
    familyForUnit,
    formatCost,
    lineCostPerBatch,
    lineCostPerPackage,
    marginFromMsrp,
    percentOf,
    pricing,
  } from "./recipe-cost-calculator.js";

  const STORAGE_KEY = "recipeCostCalculator";

  const SHIPPING_HINT = `Freight or delivery you pay on that purchase. It gets spread across
the amount you bought, exactly like the price does.`;
  const YOUR_MARGIN_HINT = `The margin you want to make on each package. Your price is the cost
per package divided by what's left after your margin.`;
  const RETAILER_MARGIN_HINT = `The margin a retailer takes. We work backwards from your price
to the shelf price that leaves everyone their cut.`;
  const MSRP_HINT = `Enter the shelf price you want and we work your margin out from it, instead
of the other way around. While it is set, your margin is whatever the MSRP leaves after cost and
everyone else's cut.`;

  let nextId = 1;

  const blankLine = (expenseType = "food") => ({
    name: "",
    expenseType,
    usedAmount: "",
    usedUnit: defaultUnitForType(expenseType),
    purchaseAmount: "",
    purchaseUnit: defaultUnitForType(expenseType),
    purchasePrice: "",
    purchaseShipping: "",
  });

  let openLines = _ref({});

  const newLine = (expenseType) => ({ id: nextId++, ...blankLine(expenseType) });

  const isOpen = (line) => Boolean(openLines.value[line.id]);

  const toggleLine = (line) => {
    openLines.value[line.id] = !isOpen(line);
  };

  const defaultState = () => ({
    currency: "USD",
    packages: 1,
    setupOpen: true,
    purchasesOpen: true,
    yourMargin: 0,
    msrpOpen: false,
    msrp: "",
    distributorMargin: 0,
    brokerMargin: 0,
    retailerMargin: 0,
    lines: [newLine("food")],
  });

  const restore = () => {
    try {
      const saved = JSON.parse(window.localStorage.getItem(STORAGE_KEY));
      if (!saved?.lines?.length) return defaultState();

      const lines = saved.lines.map((line) => ({ ...blankLine(), ...line, id: nextId++ }));
      return { ...defaultState(), ...saved, lines };
    } catch {
      return defaultState();
    }
  };

  let state = _ref(restore());

  const persist = () => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state.value));
    } catch {
      // localStorage can be blocked; the calculator still works without remembering entries
    }
  };

  watch(
    () => state.value,
    () => persist(),
    { deep: true }
  );

  const symbol = _computed(() => currencySymbol(state.value.currency));
  const breakdown = _computed(() => costBreakdown(state.value.lines, state.value.packages));
  const prices = _computed(() => pricing({ ...state.value, perPackage: breakdown.value.perPackage }));

  const packageCount = _computed(() => Math.max(Number(state.value.packages) || 0, 0));

  const buckets = _computed(() =>
    EXPENSE_TYPES.map((type) => ({
      ...type,
      cost: breakdown.value[type.value],
      perPackage: packageCount.value > 0 ? breakdown.value[type.value] / packageCount.value : 0,
      percent: percentOf(breakdown.value[type.value], breakdown.value.total),
    })).filter(({ cost }) => cost > 0)
  );

  const hasCosts = _computed(() => breakdown.value.total > 0);

  const middlemenLabel = _computed(() => {
    const distributors = Number(state.value.distributorMargin) > 0;
    const brokers = Number(state.value.brokerMargin) > 0;

    if (distributors && brokers) return "Distributors and brokers";
    if (distributors) return "Distributors";
    if (brokers) return "Brokers";
    return null;
  });

  const money = (value) => formatCost(value, state.value.currency);

  const addLine = () => state.value.lines.push(newLine());

  const removeLine = (id) => {
    state.value.lines = state.value.lines.filter((line) => line.id !== id);
    if (state.value.lines.length === 0) addLine();
  };

  const reset = () => {
    nextId = 1;
    openLines.value = {};
    state.value = defaultState();
  };

  const untouched = (line) => !line.usedAmount && !line.purchaseAmount;

  const filledIn = (line) => Boolean(line.name?.trim()) && Number(line.usedAmount) > 0;

  watch(
    () => state.value.lines,
    (lines) => {
      const last = lines.at(-1);
      if (last && filledIn(last)) state.value.lines.push(newLine(last.expenseType));
    },
    { deep: true, immediate: true }
  );

  const displayName = (line, index) => line.name?.trim() || `Item ${index + 1}`;

  const onUsedUnitChange = (line) => {
    if (familyForUnit(line.purchaseUnit) === familyForUnit(line.usedUnit)) return;

    line.purchaseUnit = line.usedUnit;
  };

  const onExpenseTypeChange = (line) => {
    if (!untouched(line)) return;

    line.usedUnit = defaultUnitForType(line.expenseType);
    line.purchaseUnit = line.usedUnit;
  };

  watch(
    () => [
      state.value.msrp,
      state.value.msrpOpen,
      state.value.distributorMargin,
      state.value.brokerMargin,
      state.value.retailerMargin,
      breakdown.value.perPackage,
    ],
    () => {
      if (!state.value.msrpOpen) return;

      const margin = marginFromMsrp({ ...state.value, perPackage: breakdown.value.perPackage });
      state.value.yourMargin = margin === null ? "" : Math.round(margin * 100) / 100;
    }
  );

  const toggleMsrp = () => {
    state.value.msrpOpen = !state.value.msrpOpen;

    if (state.value.msrpOpen) {
      state.value.msrp = prices.value.retailPrice === null ? "" : Math.round(prices.value.retailPrice * 100) / 100;
    } else {
      state.value.msrp = "";
    }
  };

  const downloadCsv = () => {
    const blob = new Blob([buildCsv(state.value)], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.download = "recipe-costs.csv";
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };
</script>

<template>
  <div class="recipe-cost-calculator">
    <section class="box">
      <button
        type="button"
        class="section-header"
        :aria-expanded="state.setupOpen"
        @click="state.setupOpen = !state.setupOpen"
      >
        <span>
          <span class="title is-4">Recipe setup</span>
          <span class="section-subtitle has-text-grey">
            What one batch makes, and what it uses.
          </span>
        </span>
        <span class="icon has-text-grey chevron" :class="{ 'is-open': state.setupOpen }">
          <i class="fas fa-chevron-down"></i>
        </span>
      </button>

      <div v-show="state.setupOpen">
        <div class="is-flex is-align-items-flex-end is-flex-wrap-wrap gap-5 mb-5">
          <div class="field mb-0">
            <label class="label" for="rcc-packages">
              <span class="icon-text">
                <span>Packages this batch makes</span>
                <IconHint
                  content="How many finished, sellable units come out of one batch. Everything
                  below is divided by this to get your cost per package."
                />
              </span>
            </label>
            <div class="control">
              <input
                id="rcc-packages"
                v-model="state.packages"
                type="number"
                class="input"
                step="any"
                min="0"
              />
            </div>
          </div>

          <div class="field mb-0">
            <label class="label" for="rcc-currency">Currency</label>
            <div class="control">
              <div class="select">
                <select id="rcc-currency" v-model="state.currency">
                  <option
                    v-for="currency in CURRENCIES"
                    :key="currency.value"
                    :value="currency.value"
                  >
                    {{ currency.symbol }} {{ currency.value }}
                  </option>
                </select>
              </div>
            </div>
          </div>
        </div>

        <div class="table-container">
          <table class="table is-fullwidth is-hoverable rcc-table">
            <thead>
              <tr>
                <th>Item</th>
                <th class="type-col">Type</th>
                <th class="amount-col">Qty/batch</th>
                <th class="unit-col">Unit</th>
                <th class="actions-col"></th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(line, index) in state.lines"
                :key="line.id"
                class="cost-line"
                :class="{ 'is-collapsed': !isOpen(line) }"
              >
                <td class="row-toggle is-hidden-tablet">
                  <button
                    type="button"
                    class="row-toggle-button"
                    :aria-expanded="isOpen(line)"
                    :aria-label="`Toggle item ${index + 1}`"
                    @click="toggleLine(line)"
                  >
                    <span>
                      <span class="stacked-label">Item</span>
                      <span :class="{ 'has-text-grey-light': !line.name?.trim() }">
                        {{ displayName(line, index) }}
                      </span>
                    </span>
                    <span class="icon has-text-grey chevron" :class="{ 'is-open': isOpen(line) }">
                      <i class="fas fa-chevron-down"></i>
                    </span>
                  </button>
                </td>
                <td>
                  <input
                    v-model="line.name"
                    type="text"
                    class="input"
                    :placeholder="`Ingredient or expense -- e.g. bread flour`"
                    :aria-label="`Item name ${index + 1}`"
                  />
                </td>
                <td>
                  <span class="stacked-label">Type</span>
                  <div class="select is-fullwidth">
                    <select
                      v-model="line.expenseType"
                      :aria-label="`Expense type ${index + 1}`"
                      @change="onExpenseTypeChange(line)"
                    >
                      <option v-for="type in EXPENSE_TYPES" :key="type.value" :value="type.value">
                        {{ type.label }}
                      </option>
                    </select>
                  </div>
                </td>
                <td>
                  <span class="stacked-label">Qty/batch</span>
                  <input
                    v-model="line.usedAmount"
                    type="number"
                    class="input"
                    step="any"
                    min="0"
                    placeholder="0"
                    :aria-label="`Amount used ${index + 1}`"
                  />
                </td>
                <td>
                  <span class="stacked-label">Unit</span>
                  <div class="select is-fullwidth">
                    <select
                      v-model="line.usedUnit"
                      :aria-label="`Unit used ${index + 1}`"
                      @change="onUsedUnitChange(line)"
                    >
                      <optgroup
                        v-for="(family, key) in UNIT_FAMILIES"
                        :key="key"
                        :label="family.label"
                      >
                        <option v-for="unit in family.units" :key="unit.value" :value="unit.value">
                          {{ unit.label }}
                        </option>
                      </optgroup>
                    </select>
                  </div>
                </td>
                <td>
                  <button
                    type="button"
                    class="button is-white has-text-grey"
                    :aria-label="`Remove item ${index + 1}`"
                    @click="removeLine(line.id)"
                  >
                    <span class="icon"><i class="fas fa-trash"></i></span>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="buttons">
          <button type="button" class="button is-primary" @click="addLine">
            <span class="icon"><i class="fas fa-plus"></i></span>
            <span>Add item</span>
          </button>
          <button type="button" class="button is-white has-text-grey" @click="reset">
            Start over
          </button>
        </div>
      </div>
    </section>

    <section class="box">
      <button
        type="button"
        class="section-header"
        :aria-expanded="state.purchasesOpen"
        @click="state.purchasesOpen = !state.purchasesOpen"
      >
        <span>
          <span class="title is-4">How you purchase ingredients</span>
          <span class="section-subtitle has-text-grey">
            The price of a quantity you actually purchase. Shipping is spread across it the same way
            the price is.
          </span>
        </span>
        <span class="icon has-text-grey chevron" :class="{ 'is-open': state.purchasesOpen }">
          <i class="fas fa-chevron-down"></i>
        </span>
      </button>

      <div v-show="state.purchasesOpen" class="table-container">
        <table class="table is-fullwidth is-hoverable rcc-table purchase-table">
          <thead>
            <tr>
              <th>Item</th>
              <th class="cost-col">Cost/batch</th>
              <th class="cost-col">Cost/package</th>
              <th class="amount-col">Price</th>
              <th class="spacer-col is-hidden-mobile"></th>
              <th class="amount-col">Quantity</th>
              <th class="spacer-col is-hidden-mobile"></th>
              <th class="unit-col">Unit</th>
              <th class="spacer-col is-hidden-mobile"></th>
              <th class="amount-col">
                <span class="icon-text">
                  <span>Shipping</span>
                  <IconHint :content="SHIPPING_HINT" />
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(line, index) in state.lines"
              :key="line.id"
              class="purchase-line"
              :class="{ 'is-collapsed': !isOpen(line) }"
            >
              <td class="row-toggle is-hidden-tablet">
                <button
                  type="button"
                  class="row-toggle-button"
                  :aria-expanded="isOpen(line)"
                  :aria-label="`Toggle item ${index + 1}`"
                  @click="toggleLine(line)"
                >
                  <span>
                    <span class="stacked-label">Item</span>
                    <span :class="{ 'has-text-grey-light': !line.name?.trim() }">
                      {{ displayName(line, index) }}
                    </span>
                  </span>
                  <span class="is-flex is-align-items-center">
                    <span class="has-text-weight-bold">{{ money(lineCostPerBatch(line)) }}</span>
                    <span class="icon has-text-grey chevron" :class="{ 'is-open': isOpen(line) }">
                      <i class="fas fa-chevron-down"></i>
                    </span>
                  </span>
                </button>
              </td>
              <td class="name-cell is-hidden-mobile">
                <span :class="{ 'has-text-grey-light': !line.name?.trim() }">
                  {{ displayName(line, index) }}
                </span>
              </td>
              <td class="cost-cell">
                <span class="stacked-label">Cost/batch</span>
                {{ money(lineCostPerBatch(line)) }}
              </td>
              <td class="cost-cell">
                <span class="stacked-label">Cost/package</span>
                {{ money(lineCostPerPackage(line, state.packages)) }}
              </td>
              <td>
                <span class="stacked-label">Price</span>
                <div class="control has-icons-left">
                  <input
                    v-model="line.purchasePrice"
                    type="number"
                    class="input"
                    step="any"
                    min="0"
                    placeholder="0"
                    :aria-label="`Purchase price ${index + 1}`"
                  />
                  <span class="icon is-small is-left">{{ symbol }}</span>
                </div>
              </td>
              <td class="spacer-col is-hidden-mobile">for</td>
              <td>
                <span class="stacked-label">Quantity</span>
                <input
                  v-model="line.purchaseAmount"
                  type="number"
                  class="input"
                  step="any"
                  min="0"
                  placeholder="0"
                  :aria-label="`Amount purchased ${index + 1}`"
                />
              </td>
              <td class="spacer-col is-hidden-mobile">&times;</td>
              <td>
                <span class="stacked-label">Unit</span>
                <div class="select is-fullwidth">
                  <select v-model="line.purchaseUnit" :aria-label="`Unit purchased ${index + 1}`">
                    <option
                      v-for="unit in compatibleUnits(line.usedUnit)"
                      :key="unit.value"
                      :value="unit.value"
                    >
                      {{ unit.label }}
                    </option>
                  </select>
                </div>
              </td>
              <td class="spacer-col is-hidden-mobile">+</td>
              <td>
                <span class="stacked-label">Shipping</span>
                <div class="control has-icons-left">
                  <input
                    v-model="line.purchaseShipping"
                    type="number"
                    class="input"
                    step="any"
                    min="0"
                    placeholder="0"
                    :aria-label="`Shipping cost ${index + 1}`"
                  />
                  <span class="icon is-small is-left">{{ symbol }}</span>
                </div>
              </td>
            </tr>
          </tbody>
          <tfoot>
            <tr>
              <td class="total-label has-text-weight-bold has-text-right">Total</td>
              <td class="cost-cell has-text-weight-bold">
                <span class="stacked-label">Cost/batch</span>
                {{ money(breakdown.total) }}
              </td>
              <td class="cost-cell has-text-weight-bold">
                <span class="stacked-label">Cost/package</span>
                {{ money(breakdown.perPackage) }}
              </td>
              <td colspan="7"></td>
            </tr>
          </tfoot>
        </table>
      </div>
    </section>

    <div class="columns">
      <div class="column is-6">
        <div class="box is-fullheight">
          <h2 class="title is-4">Cost</h2>

          <p v-if="!hasCosts" class="has-text-grey">
            Add what your batch uses and what you pay for it, and your batch and per-package costs
            show up here.
          </p>

          <template v-else>
            <div class="summary">
              <div class="summary-item">
                <span class="icon is-medium has-text-info">
                  <i class="fa-sharp-duotone fa-solid fa-lg fa-cubes"></i>
                </span>
                <div>
                  <div class="amount">{{ money(breakdown.total) }}</div>
                  <div class="is-size-7 has-text-grey">Per batch</div>
                </div>
              </div>
              <div class="summary-item">
                <span class="icon is-medium has-text-info">
                  <i class="fa-sharp-duotone fa-solid fa-lg fa-cube"></i>
                </span>
                <div>
                  <div class="amount">{{ money(breakdown.perPackage) }}</div>
                  <div class="is-size-7 has-text-grey">Per package</div>
                </div>
              </div>
            </div>

            <div class="breakdown-bar">
              <div
                v-for="bucket in buckets"
                :key="bucket.value"
                class="bar-segment"
                :class="bucket.colorClass"
                :style="{ width: `${bucket.percent}%` }"
              ></div>
            </div>

            <div class="legend">
              <div v-for="bucket in buckets" :key="bucket.value" class="legend-item">
                <div class="legend-color-bar" :class="bucket.colorClass"></div>
                <div>
                  <div class="has-text-weight-bold">{{ bucket.label }}</div>
                  <div class="is-size-7 has-text-grey">
                    {{ Math.round(bucket.percent) }}% | {{ money(bucket.perPackage) }}
                  </div>
                </div>
              </div>
            </div>
          </template>
        </div>
      </div>

      <div class="column is-6">
        <div class="box is-fullheight">
          <h2 class="title is-4">Pricing</h2>

          <div class="margin-fields">
            <div class="field">
              <label class="label is-size-7" for="rcc-your-margin">
                <span class="icon-text">
                  <span>Your margin (%)</span>
                  <IconHint :content="YOUR_MARGIN_HINT" />
                </span>
                <button type="button" class="msrp-toggle is-size-7" @click="toggleMsrp">
                  {{ state.msrpOpen ? "Set margin" : "From MSRP" }}
                </button>
              </label>
              <div class="control">
                <input
                  id="rcc-your-margin"
                  v-model="state.yourMargin"
                  type="number"
                  class="input"
                  step="any"
                  min="0"
                  max="99"
                  :readonly="state.msrpOpen"
                />
              </div>
            </div>

            <div v-if="state.msrpOpen" class="field">
              <label class="label is-size-7" for="rcc-msrp">
                <span class="icon-text">
                  <span>MSRP ({{ symbol }})</span>
                  <IconHint :content="MSRP_HINT" />
                </span>
              </label>
              <div class="control">
                <input
                  id="rcc-msrp"
                  v-model="state.msrp"
                  type="number"
                  class="input"
                  step="any"
                  min="0"
                />
              </div>
            </div>

            <div class="field">
              <label class="label is-size-7" for="rcc-distributor-margin">
                Distributor margin (%)
              </label>
              <div class="control">
                <input
                  id="rcc-distributor-margin"
                  v-model="state.distributorMargin"
                  type="number"
                  class="input"
                  step="any"
                  min="0"
                  max="99"
                />
              </div>
            </div>

            <div class="field">
              <label class="label is-size-7" for="rcc-broker-margin">Broker margin (%)</label>
              <div class="control">
                <input
                  id="rcc-broker-margin"
                  v-model="state.brokerMargin"
                  type="number"
                  class="input"
                  step="any"
                  min="0"
                  max="99"
                />
              </div>
            </div>

            <div class="field">
              <label class="label is-size-7" for="rcc-retailer-margin">
                <span class="icon-text">
                  <span>Retailer margin (%)</span>
                  <IconHint :content="RETAILER_MARGIN_HINT" />
                </span>
              </label>
              <div class="control">
                <input
                  id="rcc-retailer-margin"
                  v-model="state.retailerMargin"
                  type="number"
                  class="input"
                  step="any"
                  min="0"
                  max="99"
                />
              </div>
            </div>
          </div>

          <hr />

          <div class="has-text-weight-bold has-text-grey-darker mb-3">Pricing to</div>

          <div v-if="middlemenLabel" class="pricing-row">
            <span class="icon-text">
              <span class="icon has-text-info"><i class="fa-duotone fa-truck-fast"></i></span>
              <span>{{ middlemenLabel }}</span>
            </span>
            <span class="has-text-weight-bold">{{ money(prices.distributorPrice) }}</span>
          </div>

          <div v-if="Number(state.retailerMargin) > 0" class="pricing-row">
            <span class="icon-text">
              <span class="icon has-text-info"><i class="fa-duotone fa-shop"></i></span>
              <span>Retailers</span>
            </span>
            <span class="has-text-weight-bold">{{ money(prices.wholesalePrice) }}</span>
          </div>

          <div class="pricing-row">
            <span class="icon-text">
              <span class="icon has-text-info"><i class="fa-duotone fa-user"></i></span>
              <span>Consumers (MSRP)</span>
            </span>
            <span class="has-text-weight-bold">{{ money(prices.retailPrice) }}</span>
          </div>
        </div>
      </div>
    </div>

    <div class="is-flex is-justify-content-flex-end">
      <button type="button" class="button is-info is-outlined" @click="downloadCsv">
        <span class="icon"><i class="fas fa-download"></i></span>
        <span>Download CSV</span>
      </button>
    </div>
  </div>
</template>

<style scoped lang="scss">
  @import "~jose_modern/css";

  .section-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1rem;
    width: 100%;
    padding: 0;
    margin-bottom: 1.25rem;
    background: none;
    border: none;
    cursor: pointer;
    text-align: left;

    // Deliberately not Bulma's .subtitle: it carries a negative margin-top after a .title.
    .title,
    .section-subtitle {
      display: block;
    }

    .title {
      margin-bottom: 0.25rem;
    }

    .section-subtitle {
      font-size: $size-6;
      font-weight: normal;
    }
  }

  // Font Awesome swaps the <i> for an <svg> on load, so a bound class can no longer
  // change which icon it is. One chevron, rotated, sidesteps that entirely.
  .chevron {
    transition: transform 0.15s ease;

    &.is-open {
      transform: rotate(180deg);
    }
  }

  .row-toggle-button {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    width: 100%;
    padding: 0;
    background: none;
    border: none;
    cursor: pointer;
    text-align: left;
    font: inherit;
    color: inherit;
  }

  .rcc-table {
    td,
    th {
      vertical-align: middle;
    }

    .type-col {
      width: 10.5rem;
    }

    .amount-col {
      width: 7.5rem;
    }

    .unit-col {
      width: 8rem;
    }

    .cost-col {
      width: 8rem;
    }

    .actions-col {
      width: 1%;
    }

    .spacer-col {
      width: 1%;
      color: $grey;
      text-align: center;
      white-space: nowrap;
    }
  }

  .stacked-label {
    display: none;
    font-size: $size-7;
    font-weight: bold;
    color: $grey;
  }

  @include until($tablet) {
    .rcc-table {
      &,
      & > tbody,
      & > tbody > tr,
      & > tbody > tr > td,
      & > tfoot,
      & > tfoot > tr,
      & > tfoot > tr > td {
        display: block;
        width: auto;
      }

      & > thead {
        display: none;
      }

      & > tbody > tr {
        border: 1px solid $grey-lighter;
        border-radius: $radius;
        padding: 0.75rem;
        margin-bottom: 0.75rem;
      }

      & > tbody > tr > td,
      & > tfoot > tr > td {
        border: none;
        padding: 0.25rem 0;
      }

      & > tbody > tr.is-collapsed > td:not(.row-toggle) {
        display: none;
      }

      .stacked-label {
        display: block;
        margin-bottom: 0.25rem;
      }

      .spacer-col {
        display: none;
      }

      .name-cell {
        font-weight: bold;
      }

      .cost-cell {
        display: flex;
        justify-content: space-between;
        align-items: baseline;
      }

      & > tfoot > tr > td:empty {
        display: none;
      }

      .total-label {
        text-align: left !important;
        padding-top: 0.75rem;
      }
    }
  }

  .purchase-table {
    .name-cell,
    .cost-cell {
      white-space: nowrap;
    }

    tfoot td {
      border-top: 2px solid $grey-lighter;
    }
  }

  .summary {
    display: flex;
    gap: 1rem;
    margin-bottom: 1.25rem;

    .summary-item {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      flex: 1 1 50%;
    }

    .amount {
      font-size: $size-5;
      font-weight: bold;
    }
  }

  .breakdown-bar {
    display: flex;
    height: 12px;
    border-radius: $radius-small;
    overflow: hidden;
    background-color: $grey-lighter;
  }

  .legend {
    display: flex;
    flex-wrap: wrap;
    margin-top: 1rem;

    .legend-item {
      display: flex;
      flex: 1 1 50%;
      margin-bottom: 0.75rem;
    }

    .legend-color-bar {
      width: 5px;
      margin-right: 0.5rem;
    }
  }

  .margin-fields {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0 1rem;

    // Shared height keeps inputs aligned whether or not the label carries a hint icon
    .label {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 0.5rem;
      min-height: 1.5rem;
      margin-bottom: 0.5rem;
    }

    .msrp-toggle {
      padding: 0;
      background: none;
      border: none;
      cursor: pointer;
      color: $link;
      font-weight: normal;
      text-decoration: underline;
    }
  }

  .food-cost {
    background-color: #1ba553;
  }

  .packaging-cost {
    background-color: #b07aa1;
  }

  .labor-cost {
    background-color: #e3b448;
  }

  .overhead-cost {
    background-color: #4699c4;
  }

  .pricing-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.5rem 0;

    & + .pricing-row {
      border-top: 1px solid $grey-lighter;
    }
  }
</style>
